import { Box, Stack, Typography } from '@mui/material';
import { biomarkersApiSlice, CriteriaTime, OperatinonsSign, Operations, ResultValueType } from '@src/api';
import { FC, useContext, useEffect, useState } from 'react';
import { valueTypes } from './ResultValueRule.conts';

import { ResultPeriod } from '../ui/ResultPeriod';
import { SegmentedControl } from '@components/SegmentedControl';
import { OperationButtonWithTooltip } from '@components/OperationButtonWithTooltip';
import { useFormContext } from 'react-hook-form';
import { FormAutocompleteField, FormTextField } from '@components/FormFields';
import { RuleFactoryChildrenProps } from '../types';
import { DisableContext } from '@pages/Selection/components/DisableContext';
import { DisabledRuleItem } from '@components/DisabledRuleItem';

export const ResultValueRule: FC<RuleFactoryChildrenProps> = ({ prefix, children }) => {
  const { control, getValues, setValue, watch } = useFormContext();

  const [onlyNumbers, setOnlyNumbers] = useState(false);

  const biomarkerIdValue: string = watch(`${prefix}.loincItemId`);
  const { units } = biomarkersApiSlice.useGetUnitsByBiomarkerIdQuery(biomarkerIdValue, {
    skip: !biomarkerIdValue,
    selectFromResult: ({ data, ...rest }) => ({
      ...rest,
      units:
        data?.map(({ id, name }) => ({
          name,
          value: id,
        })) || [],
    }),
  });

  const disabled = useContext(DisableContext);

  const scaleTypeName = `${prefix}.scaleType`;

  const [scaleType, scaleValue, loincItem, rangeValues, loincUnit] = getValues([
    scaleTypeName,
    `${prefix}.scaleValue`,
    `${prefix}.loincItem`,
    `${prefix}.rangeValues`,
    `${prefix}.loincUnit`,
  ]);

  const handleClickScaleType = (value: CriteriaTime) => {
    setValue(scaleTypeName, value);
  };

  const operationValue = watch(`${prefix}.singleValue.compareOperator`);

  const singleValue = watch(`${prefix}.singleValue`);

  const unitValue = watch(`${prefix}.loincUnit`);

  const [valueType, setValueType] = useState(singleValue ? ResultValueType.Single : ResultValueType.Range);

  const onChangeOperation = (val: Operations) => {
    const onlyNumbersLocal = val !== Operations.Equals && val !== Operations.NotEqual;

    setOnlyNumbers(onlyNumbersLocal);
    setValue(`${prefix}.singleValue.compareOperator`, val);
    if (onlyNumbersLocal) setValue(`${prefix}.singleValue.value`, '');
  };

  const handleClickValueType = (valueT: ResultValueType) => {
    setValueType(valueT);

    if (valueT === ResultValueType.Single) {
      setValue(`${prefix}.singleValue.compareOperator`, Operations.Equals, { shouldValidate: true });
      setValue(`${prefix}.singleValue.value`, '');
      setValue(`${prefix}.rangeValues`, undefined, { shouldValidate: true });
    } else {
      setValue(`${prefix}.singleValue`, undefined, { shouldValidate: true });
      setValue(`${prefix}.rangeValues.from`, '');
      setValue(`${prefix}.rangeValues.to`, '');
    }
  };

  useEffect(() => {
    if (unitValue) {
      if (unitValue?.id) {
        setValue(`${prefix}.loincUnitId`, unitValue?.id);
      } else setValue(`${prefix}.loincUnitId`, null);
    }
  }, [unitValue, prefix, setValue]);

  const onClearUnits = () => {
    setValue(`${prefix}.loincUnitId`, null);
    setValue(`${prefix}.loincUnit`, null);
  };

  const decriptionValue = rangeValues
    ? `${rangeValues.from} - ${rangeValues.to}`
    : `${OperatinonsSign[singleValue?.compareOperator]}${singleValue?.value}`;

  useEffect(() => {
    setOnlyNumbers(operationValue !== Operations.Equals && operationValue !== Operations.NotEqual);
  }, [operationValue]);

  const regexOnlyNumber = new RegExp(/^\d*[,.]?\d*$/);

  return disabled ? (
    <DisabledRuleItem
      name="Result"
      value={`${scaleValue} ${CriteriaTime[scaleType].toLowerCase()}${scaleValue > 1 ? 's' : ''}`}
      valueName="In the last"
      descriptionName={loincItem?.name}
      description={`${decriptionValue} ${loincUnit?.name || ''}`}
    />
  ) : (
    <>
      <ResultPeriod
        scaleType={scaleType}
        handleClickScaleType={handleClickScaleType}
        prefix={prefix}
        disabled={disabled}
        resultType="value"
      >
        <>
          <Box maxWidth="130px" width="100%">
            <FormAutocompleteField
              control={control}
              name={`${prefix}.loincUnitId`}
              options={units || []}
              placeholder="Units"
              disabled={disabled}
              defaultValue={null}
              editValue={unitValue}
              withoutLocalValue={true}
              onClear={onClearUnits}
              disableClearIcon
            />
          </Box>

          <SegmentedControl
            values={valueTypes}
            active={valueType}
            handleClick={handleClickValueType}
            disabled={disabled}
            prefix={`${prefix}.scaleType`}
          />
          {valueType === ResultValueType.Single ? (
            <>
              <OperationButtonWithTooltip
                onChangeOperation={onChangeOperation}
                opeartionValue={operationValue}
                disabled={disabled}
              />
              <Box flexGrow={1}>
                <FormTextField
                  control={control}
                  placeholder="Value"
                  name={`${prefix}.singleValue.value`}
                  label=""
                  regex={onlyNumbers ? regexOnlyNumber : new RegExp(/^[\d,.A-Za-z]{0,100}$/)}
                  disabled={disabled}
                  withReplaceComma
                />
              </Box>
            </>
          ) : (
            <Stack direction="row" gap="18px" flexGrow={1} maxWidth="432px">
              <Stack direction="row" gap="12px" alignItems="center" maxWidth="228px">
                <Typography color={disabled ? 'grey.400' : 'grey.700'} variant="label14Bold">
                  From
                </Typography>
                <Box sx={{ maxWidth: '170px' }}>
                  <FormTextField
                    control={control}
                    name={`${prefix}.rangeValues.from`}
                    label=""
                    regex={regexOnlyNumber}
                    disabled={disabled}
                    withReplaceComma
                  />
                </Box>
              </Stack>
              <Stack direction="row" gap="12px" alignItems="center" maxWidth="209px">
                <Typography color={disabled ? 'grey.400' : 'grey.700'} variant="label14Bold">
                  To
                </Typography>
                <Box sx={{ maxWidth: '170px' }}>
                  <FormTextField
                    name={`${prefix}.rangeValues.to`}
                    label=""
                    control={control}
                    regex={regexOnlyNumber}
                    disabled={disabled}
                    withReplaceComma
                  />
                </Box>
              </Stack>
            </Stack>
          )}
          {children}
        </>
      </ResultPeriod>
    </>
  );
};
